import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Table from '../components/table/table';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import Input from '../components/input/Input';
import { getQuotationsByAssetCode, createQuotation, QuotationListItem, getAssetByCode } from '../services/QuotationsService';
import { ITableData } from '../types/interfaces';
import { useConfig } from '../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../utils/quotationAssets';

const QuotationsDetail = () => {
  const { assetCode } = useParams<{ assetCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const [quotations, setQuotations] = useState<QuotationListItem[]>([]);
  const [tableData, setTableData] = useState<ITableData>();
  const [page, setPage] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currency, setCurrency] = useState<string>('');
  
  // Form fields
  const [saleValue, setSaleValue] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  useEffect(() => {
    // Get currency from location state
    const stateCurrency = (location.state as any)?.currency;
    
    if (stateCurrency) {
      setCurrency(stateCurrency);
    } else if (assetCode) {
      // If no currency in state, fetch it by code
      fetchAssetByCode();
    }
  }, [location, assetCode]);

  const fetchAssetByCode = async () => {
    if (!assetCode) return;
    
    try {
      const asset = await getAssetByCode(assetCode);
      if (asset) {
        setCurrency(asset.name);
      } else {
        console.error('Asset not found with code:', assetCode);
      }
    } catch (error) {
      console.error('Error fetching asset by code:', error);
    }
  };

  useEffect(() => {
    if (assetCode) {
      fetchQuotations();
    }
  }, [assetCode, page]);

  const fetchQuotations = async () => {
    if (!assetCode) return;
    
    setIsLoading(true);
    try {
      const response = await getQuotationsByAssetCode(assetCode, page, pageSize);
      if (response?.data) {
        setQuotations(response.data.content);
        setTableData({
          size: response.data.size,
          page: response.data.page,
          totalElements: response.data.totalElements,
          totalPages: response.data.totalPages,
        });
      } else {
        setQuotations([]);
      }
    } catch (error) {
      console.error('Error fetching quotations:', error);
      setQuotations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    if (newPage < 0 || newPage >= (tableData?.totalPages || 0)) {
      return;
    }
    setPage(newPage);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const isFormValid = () => {
    if (!saleValue) return false;
    if (parseFloat(saleValue) <= 0) return false;
    return true;
  };

  const handleUpdate = async () => {
    if (!isFormValid() || !assetCode) return;
    
    setIsUpdating(true);
    try {
      await createQuotation(assetCode, {
        saleValue: parseFloat(saleValue),
        isChargedAutomatically: false,
      });
      
      await fetchQuotations();
      setSaleValue('');
    } catch (error) {
      console.error('Error creating quotation:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const columns = [
    {
      name: 'Valor venta',
      selector: (row: QuotationListItem) => (
        <P type='4' variant='neutral'>
          {row.saleValue !== null && row.saleValue !== undefined ? row.saleValue : '-'}
        </P>
      ),
      minWidth: '150px',
      grow: 1,
    },
    {
      name: 'Vigente desde',
      selector: (row: QuotationListItem) => (
        <P type='4' variant='neutral'>
          {row.createdAt || '-'}
        </P>
      ),
      minWidth: '200px',
      grow: 1,
    },
    {
      name: 'Vigente hasta',
      selector: (row: QuotationListItem) => (
        <P type='4' variant='neutral'>
          {row.validUntil || 'Vigente'}
        </P>
      ),
      minWidth: '200px',
      grow: 1,
    },
  ];

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing={'4'}
      height='-webkit-fill-available'
      width='85vw'
    >
      {/* Header with back button and title */}
      <Card display='flex' alignItems='center' gap='16px' margin={{ bottom: '4' }}>
        <Button variant='tertiary' onClick={handleGoBack}>
          <Icon name='arrow_left_outline' variant='tertiary' width={24} height={24} />
        </Button>
        <H withMargin={false} type='1' weight='bold' modifier='strong'>
          Cotizaciones {currency}
        </H>
      </Card>

      {/* New quotation section */}
      <Card margin={{ bottom: '4' }}>
        <H variant="secondary-2" type="3" weight="bold" modifier="strong" withMargin={false}>
          Nueva cotización
        </H>
        <Card margin={{ top: '3' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', alignItems: 'end', justifyItems: 'start'}}>
            <Input
              id="sale-value"
              type="number"
              value={saleValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setSaleValue('');
                  return;
                }
                const regex = /^\d*\.?\d{0,2}$/;
                if (regex.test(value)) {
                  setSaleValue(value);
                }
              }}
              label={`Valor venta (${localCurrencyCode})`}
              placeholder="0.00"
              disabled={isUpdating}
              min="0.01"
              step="0.01"
            />
            
            <Button
              variant='primary'
              onClick={handleUpdate}
              disabled={!isFormValid() || isUpdating}
            >
              {isUpdating ? 'Actualizando...' : 'Actualizar'}
            </Button>
          </div>
        </Card>
      </Card>

      {/* Quotations table */}
      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : quotations.length > 0 ? (
        <Card margin={{ top: '4' }} justifyContent='center' alignItems='center'>
          <Table
            columns={columns}
            data={quotations}
            tableData={tableData}
            handleChangePage={(e: any) => handleChangePage(e)}
          />
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No hay cotizaciones disponibles para esta moneda.
          </P>
        </Card>
      )}
    </Card>
  );
};

export default QuotationsDetail;
